import { useChatWidgetPositionStore } from '@cobliteam/hooks/src/useChatWidget/store';
import { useUnresolvedIncidents } from '@cobliteam/hooks/src/useEntity/useStatusPage';
import Link from '@cobliteam/ui/components/Link';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useEffect, useRef } from 'react';
import UpdateVersion from '../../../../UpdateVersion';
import InfoCobli from './InfoCobli';

const COBLI_URL = 'https://cobli.co';
const TERMS_OF_USE_URL = 'https://cobli-contratos-e-termos.s3.amazonaws.com/uso_da_plataforma/TERMOS+DE+USO+DA+PLATAFORMA+COBLI+-+2023-04-27.pdf';
const SERVICE_CONTRACT_URL = 'https://cobli-contratos-e-termos.s3.amazonaws.com/contrato_de_servico/contrato_prestacao_de_servicos.pdf';
const STATUS_URL = 'https://www.coblistatus.com/';
const API_URL = 'https://docs.cobli.co';
const SUPPORT_URL = 'https://suporte.cobli.co/';

const linkProps = {
  target: '_blank',
  rel: 'noreferrer',
  className: 'text-gray-9 no-underline font-medium hover:text-gray-11 active:text-gray-11 focus:text-gray-9 text-xs',
};

const leftLinks = [
  { href: TERMS_OF_USE_URL, label: 'Termos de Uso' },
  { href: SERVICE_CONTRACT_URL, label: 'Contrato de Serviço' },
];

const PageFooter = () => {
  const setFooterVisible = useChatWidgetPositionStore((s) => s.setFooterVisible);
  const { data: incidentsData } = useUnresolvedIncidents();

  const hasIncidents = incidentsData?.incidents && incidentsData.incidents.length > 0;
  const statusColor = hasIncidents ? 'bg-warning' : 'bg-success';

  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!footerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setFooterVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observer.observe(footerRef.current);
    return () => {
      observer.disconnect();
      setFooterVisible(false);
    };
  }, [setFooterVisible]);

  return (
    <>
      <div ref={footerRef} id='page-footer' className='relative grid grid-cols-1 laptop:grid-cols-3 gap-5 bg-gray-2 px-5 py-2'>
        <div className='laptop:order-2 order-1 col-span-1 flex items-center justify-start laptop:justify-center'>
          <Link href={COBLI_URL} {...linkProps}>
            <Icon name='cb-logo-cobli-name' size='xl' className='hover:text-brand' />
          </Link>
        </div>

        <div className='laptop:order-1 order-2 col-span-1 flex laptop:flex-row flex-col items-start laptop:items-center gap-2 laptop:gap-4'>
          {leftLinks.map((link) => (
            <Link key={link.href} href={link.href} {...linkProps}>
              {link.label}
            </Link>
          ))}
        </div>

        <div className='laptop:order-3 order-3 col-span-1 flex laptop:flex-row flex-col items-start laptop:items-center laptop:justify-end gap-2 laptop:gap-4'>
          <Link key={STATUS_URL} href={STATUS_URL} className={cn('flex items-center gap-1', linkProps.className)} target='_blank' rel='noreferrer'>
            <div className={`size-1.5 rounded-full ${statusColor}`} />
            <span className='text-xs'>Status</span>
          </Link>
          <Link key={API_URL} href={API_URL} {...linkProps}>
            API
          </Link>
          <Link key={SUPPORT_URL} href={SUPPORT_URL} {...linkProps}>
            Suporte
          </Link>

          <div className='self-end laptop:self-center'>
            <UpdateVersion sidebarOpen={false} />
          </div>
        </div>
      </div>
      <InfoCobli />
    </>
  );
};

export default PageFooter;
